import { QuotaType } from '@bpinternal/const'
import { AsyncButton, Separator, Skeleton } from '@bpinternal/ui-kit-next'
import { Sparkles } from 'lucide-react'
import { Boundary } from '~/components'
import { useDetailedUsage } from '~/hooks'
import { trackEvent } from '~/providers/SegmentProvider'
import { formatters, NANO_DOLLARS_PER_DOLLAR } from '~/utils'
import { useAddons } from '../hooks/useAddons'
import { useCancelSubscription } from '../hooks/useCancelSubscription'
import { usePlanProducts } from '../hooks/usePlanProducts'
import { showCancelSubscriptionReasonDialog } from './CancelSubscriptionReasonDialog'
import { showCancelSubscriptionConfirmDialog } from './CancelSubscriptionConfirmDialog'
import { PlanIcon } from './PlanIcon'
import { ProductIcon } from './ProductIcon'
import { Plan } from '../types'
import { getOrderedPlansInfo } from '../utils/plan'
import { useSuspenseQuery } from '~/services'

type UpcomingPaymentSummaryProps = {
  workspaceId: string
}

export const UpcomingPaymentSummary = ({ workspaceId }: UpcomingPaymentSummaryProps) => {
  const {
    data: { billingReadonly },
  } = useSuspenseQuery('workspaces_/$workspaceId_/billing/info', { workspaceId })
  const { addons } = useAddons(workspaceId)
  const { nextPhasePlan, currentPlan } = usePlanProducts(workspaceId)

  const { mutate: cancelSubscription, isPending } = useCancelSubscription(
    workspaceId,
    () => {
      trackEvent({
        type: 'cancel_subscription',
        workspaceId,
        plan: currentPlan.legacyName,
        state: 'success',
        success: true,
      })
    },
    () => {
      trackEvent({
        type: 'cancel_subscription',
        workspaceId,
        plan: currentPlan.legacyName,
        state: 'errored',
        success: false,
      })
    }
  )

  const currentDate = new Date()
  const nextBillingDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)

  let upcomingPlan: { legacyName: Plan; pricePerMonth: number | undefined }
  if (currentPlan.cycle === 'yearly') {
    upcomingPlan = {
      legacyName: currentPlan.legacyName,
      pricePerMonth: undefined,
    }
  } else if (currentPlan.cycle === 'monthly' && !nextPhasePlan) {
    upcomingPlan = {
      legacyName: currentPlan.legacyName,
      pricePerMonth: currentPlan.pricePerMonth,
    }
  } else if (nextPhasePlan) {
    upcomingPlan = {
      legacyName: nextPhasePlan.legacyName,
      pricePerMonth: nextPhasePlan.pricePerMonth,
    }
  } else {
    upcomingPlan = {
      legacyName: 'community',
      pricePerMonth: 0,
    }
  }

  const availablePlans = getOrderedPlansInfo()

  async function handleCancelSubscription() {
    const confirm = await showCancelSubscriptionConfirmDialog({ workspaceId })
    if (!confirm) {
      return
    }

    const cancelReason = await showCancelSubscriptionReasonDialog()
    if (!cancelReason) {
      return
    }

    cancelSubscription(cancelReason)
  }

  return (
    <div className="flex h-full flex-col justify-between rounded-xl bg-gray-2 p-6">
      <div className="mb-12 lg:mb-0">
        <div className="mb-6 flex flex-col gap-3">
          <div className="font-semibold">Upcoming Payment</div>
          <Boundary suspenseFallback={<Skeleton />}>
            <UpcomingPayment workspaceId={workspaceId} upcomingPlan={upcomingPlan} />
          </Boundary>
          <div className="text-xs font-semibold text-muted-foreground">
            Will be billed on{' '}
            {nextBillingDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </div>
        </div>
        <div className="flex flex-col gap-4 text-sm ">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PlanIcon plan={upcomingPlan.legacyName} />
              <div className="text-muted-foreground">
                {upcomingPlan.legacyName === 'enterprise'
                  ? 'Enterprise'
                  : availablePlans[upcomingPlan.legacyName].displayName}
              </div>
            </div>
            {upcomingPlan.pricePerMonth !== undefined && (
              <div className="text-foreground">{formatters.$centdollars(upcomingPlan.pricePerMonth)}</div>
            )}
          </div>
          <Separator />
          <div className="flex justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="rounded-lg bg-purple-9 p-1 text-white" />
              <div className="text-muted-foreground">AI Spend</div>
            </div>
            <Boundary suspenseFallback={<Skeleton className="h-9 w-full" />}>
              <AiSpend workspaceId={workspaceId} />
            </Boundary>
          </div>
          {addons.some((addon) => addon.nextPaymentQuantity > 0) && (
            <>
              <Separator />
              <div className="font-semibold text-muted-foreground">Add-ons</div>
              {addons
                .filter((addon) => addon.nextPaymentQuantity > 0)
                .map((addon) => {
                  return (
                    <div key={addon.productId} className="flex justify-between">
                      <div className="flex gap-2">
                        <ProductIcon
                          productQuotaName={addon.quotaName as QuotaType}
                          className="rounded-lg bg-blue-8 p-1 text-white"
                        />
                        <div className="text-muted-foreground">{addon.displayName}</div>
                        <div className="text-muted-foreground">{`x${addon.nextPaymentQuantity}`}</div>
                      </div>
                      <div>{formatters.$centdollars(addon.priceCents * addon.nextPaymentQuantity)}</div>
                    </div>
                  )
                })}
            </>
          )}
        </div>
      </div>
      {currentPlan.legacyName === 'enterprise' ? (
        <a href="https://botpress.com/contact-us" target="_blank" rel="noreferrer">
          <AsyncButton loading={isPending} disabled={isPending} variant="secondary" className="w-full">
            Cancel Subscription
          </AsyncButton>
        </a>
      ) : (
        <AsyncButton
          loading={isPending}
          disabled={
            isPending ||
            billingReadonly ||
            ((nextPhasePlan?.legacyName === 'community' || currentPlan.cancelAtPeriodEnd) &&
              !addons.some((addon) => addon.nextPaymentQuantity > 0))
          }
          onClick={handleCancelSubscription}
          variant="secondary"
        >
          Cancel Subscription
        </AsyncButton>
      )}
    </div>
  )
}

const AiSpend = ({ workspaceId }: { workspaceId: string }) => {
  const { ai_spend } = useDetailedUsage({
    workspaceId,
    quotas: ['ai_spend'],
    period: new Date().toISOString().split('T')[0],
  })

  const nextAiSpendPrice = Math.max(ai_spend.value / NANO_DOLLARS_PER_DOLLAR - 5, 0)

  return <div className="text-foreground">{formatters.$dollars(nextAiSpendPrice)}</div>
}

const UpcomingPayment = ({
  workspaceId,
  upcomingPlan,
}: {
  workspaceId: string
  upcomingPlan: { legacyName: Plan; pricePerMonth: number | undefined }
}) => {
  const { addons } = useAddons(workspaceId)
  const { ai_spend } = useDetailedUsage({
    workspaceId,
    quotas: ['ai_spend'],
    period: new Date().toISOString().split('T')[0],
  })

  const nextAddonPrice = addons.reduce((acc, addon) => acc + addon.priceCents * addon.nextPaymentQuantity, 0)
  const nextAiSpendPrice = Math.max(ai_spend.value / NANO_DOLLARS_PER_DOLLAR - 5, 0) * 100 // Transform to cent dollars
  const upcomingInvoiceTotal = nextAddonPrice + nextAiSpendPrice + (upcomingPlan.pricePerMonth ?? 0)

  return <div className="text-3xl font-semibold">{formatters.$centdollars(upcomingInvoiceTotal)}</div>
}
